import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Radio, Plus, Search, RefreshCw, SearchX } from 'lucide-react';
import {
  Button,
  Badge,
  Input,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import { PageHeader, Toolbar, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useDrmStreams,
  useStopStream,
  useDeleteStream,
  type DrmStream,
  type StreamKind,
  type StreamStatus,
} from '@/api/aes';
import { useStreamColumns } from './stream-columns';

type KindFilter = StreamKind | 'all';
type StatusFilter = StreamStatus | 'all';

/** Sources & Streams catalog — live + VOD streams from sig-control (owner-scoped). */
export function StreamList({ base }: { base: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const { data, isLoading, isError, error, refetch, isFetching } = useDrmStreams();

  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [pendingStop, setPendingStop] = useState<DrmStream | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DrmStream | null>(null);

  const stop = useStopStream();
  const del = useDeleteStream();
  const busyId = stop.isPending ? stop.variables : del.isPending ? del.variables : null;

  const all = useMemo(() => data ?? [], [data]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((s) => {
      if (kind !== 'all' && s.kind !== kind) return false;
      if (status !== 'all' && s.status !== status) return false;
      if (q && !(s.id.toLowerCase().includes(q) || s.app.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [all, query, kind, status]);

  const columns = useStreamColumns({
    busyId,
    onStop: (s) => setPendingStop(s),
    onDelete: (s) => setPendingDelete(s),
  });

  if (isLoading && !data) return <Skeleton variant="rect" height={300} />;

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Couldn't load streams"
        actions={
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        {(error as { message?: string })?.message ?? 'The control plane is temporarily unavailable.'}
      </Alert>
    );
  }

  return (
    <>
      <PageHeader
        icon={<Radio size={20} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>DRM Management</Link></BreadcrumbItem>
            <BreadcrumbItem current>Sources & Streams</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Sources & Streams"
        description="LIVE channels and VOD assets protected by HLS AES-128."
        actions={<Badge tone="accent" appearance="outline">BETA</Badge>}
      />

      {all.length > 0 && (
        <Toolbar
          start={
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Input
                placeholder="Search streams…"
                startSlot={<Search size={15} />}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ maxWidth: 280 }}
              />
              <Select value={kind} onValueChange={(v) => setKind(v as KindFilter)}>
                <SelectTrigger style={{ minWidth: 130 }}>
                  <SelectValue placeholder="All kinds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All kinds</SelectItem>
                  <SelectItem value="live">LIVE</SelectItem>
                  <SelectItem value="vod">VOD</SelectItem>
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
                <SelectTrigger style={{ minWidth: 150 }}>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="provisioning">Provisioning</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="stopped">Stopped</SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
          end={
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Button variant="secondary" startIcon={<RefreshCw size={15} />} disabled={isFetching} onClick={() => void refetch()}>
                Refresh
              </Button>
              <Button startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/streams/new`)}>
                Add source
              </Button>
            </div>
          }
        />
      )}

      <DataTable
        columns={columns}
        data={rows}
        getRowId={(s) => s.id}
        onRowClick={(s) => navigate(`${base}/streams/${encodeURIComponent(s.id)}`)}
        emptyState={
          all.length === 0 ? (
            <EmptyState
              icon={<Radio size={20} />}
              title="No streams yet"
              description="Provision your first LIVE channel or VOD asset to start delivering AES-128 protected playback."
              actions={
                <Button size="sm" startIcon={<Plus size={14} />} onClick={() => navigate(`${base}/streams/new`)}>
                  Add source
                </Button>
              }
            />
          ) : (
            <EmptyState icon={<SearchX size={20} />} title="No matching streams" description="Adjust the search or filters." />
          )
        }
        footer={rows.length ? `Showing ${rows.length} of ${all.length} stream${all.length === 1 ? '' : 's'}` : undefined}
      />

      <ConfirmDialog
        open={pendingStop != null}
        onOpenChange={(o) => !o && setPendingStop(null)}
        title={`Stop "${pendingStop?.id}"?`}
        description="The packager stops encrypting and the live edge ends. Viewers lose playback. DVR content remains for the configured window."
        confirmLabel="Stop stream"
        destructive
        loading={stop.isPending}
        onConfirm={() => {
          if (!pendingStop) return;
          const s = pendingStop;
          stop.mutate(s.id, {
            onSuccess: () => {
              show({ tone: 'success', title: `Stream "${s.id}" stopped` });
              setPendingStop(null);
            },
            onError: (e) => {
              show({ tone: 'danger', title: (e as Error)?.message || "Couldn't stop stream" });
              setPendingStop(null);
            },
          });
        }}
      />

      <ConfirmDialog
        open={pendingDelete != null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title={`Delete "${pendingDelete?.id}"?`}
        description="The stream registration, shard assignment and key policy are removed. This cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          const s = pendingDelete;
          del.mutate(s.id, {
            onSuccess: () => {
              show({ tone: 'success', title: `Stream "${s.id}" deleted` });
              setPendingDelete(null);
            },
            onError: (e) => {
              show({ tone: 'danger', title: (e as Error)?.message || "Couldn't delete stream" });
              setPendingDelete(null);
            },
          });
        }}
      />
    </>
  );
}

export default StreamList;
